import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import visionLiveLogo from '../assets/images/visionlive.png';

const Header = ({ onBack }) => (
  <motion.nav
    initial={{ y: -20, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.3 }}
    className="sticky top-0 z-50 bg-[#0d0d0d]/90 backdrop-blur-md border-b border-white/5"
  >
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5 text-white/60" />
          </button>
        )}
        <a href="/" className="flex items-center gap-2">
          <img
            src={visionLiveLogo}
            alt="VisionLive"
            className="h-9 w-auto max-w-[220px] object-contain"
          />
        </a>
      </div>
    </div>
  </motion.nav>
);

const Section = ({ title, id, children }) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="text-lg font-semibold text-white mb-4">{title}</h2>
    <div className="text-white/70 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPolicyPage = ({ onBack }) => {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#cookies') {
      const el = document.getElementById('cookies');
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white font-sans antialiased">
      <Header onBack={onBack} />
      <motion.main
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="max-w-3xl mx-auto px-6 py-12"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-white/50 text-sm mb-10">
          Last updated: January 2026 · VisionLive is operated by Ferid Brković and VTR Group d.o.o.
        </p>

        <Section title="1. Controller">
          <p>
            The VisionLive website, virtual try-on (VTO) software, API, widget, and dashboard
            (the “Service”) are operated by <strong>Ferid Brković and VTR Group d.o.o.</strong>.
            We are the data controller for personal data processed in connection with the Service.
            Subscriptions are processed via Whop.com; their privacy practices apply to payment
            and account data they handle.
          </p>
        </Section>

        <Section title="2. Data we collect">
          <p>We may collect:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account data:</strong> name, email, profile image (e.g. from Google OAuth), and Whop.com identifiers when your access is provisioned through approved onboarding.</li>
            <li><strong>Usage data:</strong> hashed IP, page/product views, and similar metrics when you use the dashboard or embedded VTO widget (if you have agreed to analytics).</li>
            <li><strong>Data stored in your browser:</strong> preferences, snapshots, selfies, and pose clips you create in the VTO. These are kept in cookies, localStorage, and IndexedDB on your device (see <a href="#cookies" className="text-[#3B82F6] hover:underline">Cookies &amp; local storage</a>).</li>
          </ul>
        </Section>

        <Section title="3. Purposes and legal basis">
          <p>
            We use your data to provide the Service (authentication, VTO functionality, subscriptions,
            support), to improve and secure the Service, and—only if you consent—for analytics.
            Legal bases: <strong>performance of a contract</strong> (account, subscription, core
            VTO), <strong>legitimate interest</strong> (security, fraud prevention, essential
            cookies/storage), and <strong>consent</strong> (optional analytics).
          </p>
        </Section>

        <Section title="4. Cookies and local storage" id="cookies">
          <p>
            The VTO software needs <strong>cookies</strong>, <strong>localStorage</strong>, and{' '}
            <strong>IndexedDB</strong> to work (e.g. session, preferences, selfies, video clips).
            You can reject optional analytics; essential storage cannot be disabled without
            losing core functionality.
          </p>
          <p className="font-medium text-white/90 mt-4 mb-2">Essential (required for VTO)</p>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="py-2 pr-4 text-white/80">Type</th>
                  <th className="py-2 pr-4 text-white/80">Name / key</th>
                  <th className="py-2 text-white/80">Purpose</th>
                </tr>
              </thead>
              <tbody className="text-white/70">
                <tr className="border-b border-white/10"><td className="py-2 pr-4">Cookies</td><td className="py-2 pr-4">better-auth.session_token, session_data, __session</td><td className="py-2">Authentication</td></tr>
                <tr className="border-b border-white/10"><td className="py-2 pr-4">localStorage</td><td className="py-2 pr-4">vto:selectedGlassesId, vto:mobileZoomFactor</td><td className="py-2">VTO preferences</td></tr>
                <tr className="border-b border-white/10"><td className="py-2 pr-4">localStorage</td><td className="py-2 pr-4">vto:snapshots, vto:selfies</td><td className="py-2">Saved snapshots and selfies</td></tr>
                <tr className="border-b border-white/10"><td className="py-2 pr-4">IndexedDB</td><td className="py-2 pr-4">vto_db (store: video_clips)</td><td className="py-2">Pose clips / recordings</td></tr>
                <tr className="border-b border-white/10"><td className="py-2 pr-4">sessionStorage</td><td className="py-2 pr-4">pendingPlan, pendingBilling, mn_vto_unlocked_*</td><td className="py-2">Auth flow, access gate</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4">
            We use a cookie-consent banner to let you <strong>Accept all</strong>,{' '}
            <strong>Reject optional</strong>, or <strong>Customize</strong> (e.g. analytics).
            Essential storage is always used; analytics is optional and only enabled with your consent.
          </p>
        </Section>

        <Section title="5. Retention">
          <p>
            Account and subscription data are kept while your account is active and as needed for
            legal or support purposes afterward. Analytics data (e.g. hashed IP, counts) are
            aggregated and retained as needed to operate the Service. Data in cookies, localStorage,
            and IndexedDB on your device remains until you clear it or we update the app (e.g. key
            changes); you can clear it anytime via your browser.
          </p>
        </Section>

        <Section title="6. Your rights (GDPR)">
          <p>
            If you are in the EEA/UK, you have the right to <strong>access</strong>,{' '}
            <strong>rectification</strong>, <strong>erasure</strong>, <strong>restriction</strong>,{' '}
            <strong>data portability</strong>, and to <strong>object</strong> to processing. You
            may also <strong>withdraw consent</strong> (e.g. for analytics) at any time via our
            cookie preferences or by clearing local data. You can lodge a complaint with a
            supervisory authority.
          </p>
        </Section>

        <Section title="7. Sharing">
          <p>
            We do not sell your data. We may share it with service providers (e.g. hosting,
            Whop.com for subscriptions) only as needed to run the Service. We may disclose
            data if required by law or to protect our rights and safety.
          </p>
        </Section>

        <Section title="8. Security">
          <p>
            We use reasonable measures to protect your data (e.g. HTTPS, hashing where
            appropriate). No system is completely secure; you use the Service at your own risk.
          </p>
        </Section>

        <Section title="9. Changes">
          <p>
            We may update this Privacy Policy from time to time. We will post the updated
            version on this page and update the “Last updated” date. Continued use after
            changes constitutes acceptance. We encourage you to review it periodically.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            For privacy-related requests or questions, contact us through the channels
            provided on the VisionLive website or via the Whop.com platform. For GDPR
            requests, we will respond within a reasonable timeframe.
          </p>
        </Section>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-wrap gap-x-6 gap-y-2">
          <a href="/terms" className="text-[#3B82F6] hover:underline text-sm">Terms of Use</a>
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} VisionLive. All rights reserved. Ferid Brković and VTR Group d.o.o.
          </p>
        </div>
      </motion.main>
    </div>
  );
};

export default PrivacyPolicyPage;
